<template>
  <div>
    <div v-if="isLoading || !isMinimumLoadingTime" class="relative">
      <img
        src="https://images.ctfassets.net/wmwzystk5o45/10wQtroaMAsqug9haLksrh/117b45d0e8e38fa2be90078eaa5c0696/Screenshot_2026-01-23_at_5.09.02%C3%A2__PM.png?fm=avif&w=500&q=100"
        class="w-full"
        alt=""
      />
      <Card class="card-preserve !absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 p-24 !rounded-4xl">
        <div class="flex gap-8 items-center whitespace-nowrap">
          <LoadingSpinner />
          <p class="text-h3 font-regular">Finding sitters</p>
        </div>
      </Card>
    </div>
    <Card v-else>
      <AlertBanner :auto-scroll-to-banner="false" :inline-banner="true" :status="HelperStatus.Neutral" class="mb-16">
        <template #icon>
          <PlaceOutlineIcon />
        </template>
        <p v-if="sitterCount >= 10" class="text-strong-body">
          {{ sitterCount }} sitters <span class="text-body font-regular">near you!</span>
        </p>
        <p v-else class="text-body font-regular">We found sitters near you!</p>
      </AlertBanner>
      <img
        src="https://images.ctfassets.net/wmwzystk5o45/48G0OtdrTi83EevqZsw3r3/8e7feeec6d0397cb7d5b50bdff7503ee/Group_10127311_4x.png?fm=avif&w=500&q=100"
        class="w-full h-auto max-w-[317px] mx-auto mb-16"
        alt=""
      />
      <h1 class="text-h2 font-regular mb-24">Finding care starts here</h1>
      <IconList :items="listItems" icon-color="#FF0064" :custom-margin="24" class="mb-32" />
      <div class="flex flex-row-reverse mt-8">
        <RouteStepperNextButton variant="primary" size="large" class="w-full md:max-w-none" value="Continue" />
      </div>
    </Card>
  </div>
</template>

<script lang="ts">
import { PropType } from 'vue';
import { format, subWeeks } from 'date-fns';
import Card from '@/components/card/Card.vue';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import IconList from '@/components/icon_list/IconList.vue';
import { HelperStatus } from '@/helpers/status';
import AccountOutlineIcon from '@/graphics/icons/AccountOutlineIcon.vue';
import JobsIcon from '@/graphics/icons/JobsIcon.vue';
import MessageSentOutlineIcon from '@/graphics/icons/MessageSentOutlineIcon.vue';
import PlaceOutlineIcon from '@/graphics/icons/PlaceOutlineIcon.vue';
import onboardingQueries from '@/graphql/onboarding/queries';
import LoadingSpinner from '@/components/spinner/Spinner.vue';
import { OnboardingSeekerDraft } from '../types';
import RouteStepperNextButton from '@/components/route_stepper/RouteStepperNextButton.vue';

export default {
  name: 'OnboardingSeekerCareNearYou',
  components: { Card, AlertBanner, IconList, PlaceOutlineIcon, LoadingSpinner, RouteStepperNextButton },
  props: {
    draft: {
      type: Object as PropType<OnboardingSeekerDraft>,
      required: true,
    },
  },
  data(): {
    sitterCount: number;
    isLoading: boolean;
    isMinimumLoadingTime: boolean;
  } {
    return {
      sitterCount: 0,
      isLoading: true,
      isMinimumLoadingTime: false,
    };
  },
  computed: {
    HelperStatus(): typeof HelperStatus {
      return HelperStatus;
    },
    listItems() {
      return [
        { icon: JobsIcon, text: 'Tell us what kind of care you need' },
        { icon: AccountOutlineIcon, text: 'Browse sitter profiles near you' },
        { icon: MessageSentOutlineIcon, text: "Connect with sitters you're interested in" },
      ];
    },
  },
  activated() {
    this.isMinimumLoadingTime = false;
    const MIN_LOADING_TIME = 2000; // 2 seconds in ms
    setTimeout(() => {
      this.isMinimumLoadingTime = true;
    }, MIN_LOADING_TIME);
  },
  apollo: {
    sitterSearch: {
      query: onboardingQueries.FETCH_SITTER_COUNT,
      variables(): any {
        return {
          geoPointX: this.draft.location?.x,
          geoPointY: this.draft.location?.y,
          rate: 2000,
          distance: 15,
          // last login within the last 6 weeks
          lastLoginAt: format(subWeeks(new Date(), 6), 'yyyy-MM-dd'),
        };
      },
      skip(): boolean {
        if (!this.draft.location?.x || !this.draft.location?.y) {
          this.isLoading = false;
          return true;
        }
        return false;
      },
      result({ data, loading }) {
        if (!loading) {
          this.isLoading = false;
          this.sitterCount = data.sitterSearch.totalCount;
        }
      },
      error(error) {
        this.isLoading = false;
        // eslint-disable-next-line no-console
        console.error('Error fetching sitter count:', error);
      },
    },
  },
};
</script>
